import { Tag as Tag$1 } from '../../Context/dist/effect-Context.esm.js';
import { pipe } from '../../Function/dist/effect-Function.esm.js';
import { commit, map, flatMap, succeed, zipRight } from './core.esm.js';
import { forEach } from './stm.esm.js';
import { toArray, fromIterable, updateSTM, get, update } from './tArray.esm.js';
import { make, modify } from './tRef.esm.js';
import { effect } from '../../Layer/dist/effect-Layer.esm.js';
import { PCGRandom } from '../../Utils/dist/effect-Utils.esm.js';

const TRandomSymbolKey = "effect/TRandom";

/** @internal */
const TRandomTypeId = /*#__PURE__*/Symbol.for(TRandomSymbolKey);
const randomInteger = state => {
  const prng = new PCGRandom();
  prng.setState(state);
  return [prng.integer(0), prng.getState()];
};
const randomIntegerBetween = (low, high) => {
  return state => {
    const prng = new PCGRandom();
    prng.setState(state);
    return [prng.integer(high - low) + low, prng.getState()];
  };
};
const randomNumber = state => {
  const prng = new PCGRandom();
  prng.setState(state);
  return [prng.number(), prng.getState()];
};
const withState = (state, f) => {
  return pipe(state, modify(f));
};
const shuffleWith = (iterable, nextIntBounded) => {
  const swap = (buffer, index1, index2) => pipe(buffer, get(index1), flatMap(tmp => pipe(buffer, updateSTM(index1, () => pipe(buffer, get(index2))), zipRight(pipe(buffer, update(index2, () => tmp))))));
  return pipe(fromIterable(iterable), flatMap(buffer => {
    const array = [];
    for (let i = array.length; i >= 2; i = i - 1) {
      array.push(i);
    }
    return pipe(array, forEach(n => pipe(nextIntBounded(n), flatMap(k => swap(buffer, n - 1, k))), {
      discard: true
    }), zipRight(toArray(buffer)));
  }));
};

/** @internal */
const Tag = /*#__PURE__*/Tag$1();
class TRandomImpl {
  [TRandomTypeId] = TRandomTypeId;
  constructor(state) {
    this.state = state;
    this.next = withState(this.state, randomNumber);
    this.nextBoolean = flatMap(this.next, n => succeed(n > 0.5));
    this.nextInt = withState(this.state, randomInteger);
  }
  nextRange(min, max) {
    return flatMap(this.next, n => succeed((max - min) * n + min));
  }
  nextIntBetween(low, high) {
    return withState(this.state, randomIntegerBetween(low, high));
  }
  shuffle(elements) {
    return shuffleWith(elements, n => this.nextIntBetween(0, n));
  }
}

/** @internal */
const live = /*#__PURE__*/effect(Tag, /*#__PURE__*/pipe( /*#__PURE__*/make( /*#__PURE__*/new PCGRandom(Math.random() * 4294967296 >>> 0).getState()), /*#__PURE__*/map(seed => new TRandomImpl(seed)), commit));

/** @internal */
const next = /*#__PURE__*/flatMap(Tag, random => random.next);

/** @internal */
const nextBoolean = /*#__PURE__*/flatMap(Tag, random => random.nextBoolean);

/** @internal */
const nextInt = /*#__PURE__*/flatMap(Tag, random => random.nextInt);

/** @internal */
const nextIntBetween = (low, high) => flatMap(Tag, random => random.nextIntBetween(low, high));

/** @internal */
const nextRange = (min, max) => flatMap(Tag, random => random.nextRange(min, max));

/** @internal */
const shuffle = elements => flatMap(Tag, random => random.shuffle(elements));

export { TRandomTypeId, Tag, live, next, nextBoolean, nextInt, nextIntBetween, nextRange, shuffle };
